import React, { useEffect, useMemo, useState, useRef, Fragment } from 'react';
import { RowData, Inputs } from '../utils/roiCalculations';
import { fmtMoney } from '../utils/formatters';
interface ProjectionTableProps {
  rows: RowData[];
  inputs: Inputs;
  onInputsChange: (inputs: Inputs) => void;
  halfSales: boolean;
  setHalfSales: (val: boolean) => void;
  doubleAttrition: boolean;
  setDoubleAttrition: (val: boolean) => void;
}
export function ProjectionTable({
  rows,
  inputs,
  onInputsChange,
  halfSales,
  setHalfSales,
  doubleAttrition,
  setDoubleAttrition
}: ProjectionTableProps) {
  const [editingAdSpend, setEditingAdSpend] = useState<number | null>(null);
  const [editAdSpendValue, setEditAdSpendValue] = useState<string>('');
  const [timeframe, setTimeframe] = useState<3 | 6 | 12>(6);
  const [expandedSections, setExpandedSections] = useState({
    recruiting: true,
    activeReps: true,
    investment: true,
    revenue: true
  });
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  const checkScrollButtons = () => {
    if (scrollContainerRef.current) {
      const {
        scrollLeft,
        scrollWidth,
        clientWidth
      } = scrollContainerRef.current;
      setCanScrollLeft(scrollLeft > 0);
      setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 10);
    }
  };
  const scroll = (direction: 'left' | 'right') => {
    if (scrollContainerRef.current) {
      const scrollAmount = 300;
      const newScrollLeft = scrollContainerRef.current.scrollLeft + (direction === 'right' ? scrollAmount : -scrollAmount);
      scrollContainerRef.current.scrollTo({
        left: newScrollLeft,
        behavior: 'smooth'
      });
      setTimeout(checkScrollButtons, 300);
    }
  };
  const toggleSection = (section: keyof typeof expandedSections) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };
  const handleAdSpendChange = (monthIndex: number, value: number) => {
    if (monthIndex === 0) return;
    const arrayIndex = monthIndex - 1;
    const newMonthlyAdSpend = [...(inputs.monthlyAdSpend || Array(12).fill(inputs.adSpend))];
    newMonthlyAdSpend[arrayIndex] = Math.max(0, value);
    onInputsChange({
      ...inputs,
      monthlyAdSpend: newMonthlyAdSpend
    });
  };
  const handlePushAdSpendRight = (monthIndex: number) => {
    if (monthIndex === 0) return;
    const arrayIndex = monthIndex - 1;
    const currentMonthlyAdSpend = inputs.monthlyAdSpend || Array(12).fill(inputs.adSpend);
    const valueToPropagate = currentMonthlyAdSpend[arrayIndex];
    const newMonthlyAdSpend = currentMonthlyAdSpend.map((val, idx) => {
      return idx >= arrayIndex ? valueToPropagate : val;
    });
    onInputsChange({
      ...inputs,
      monthlyAdSpend: newMonthlyAdSpend
    });
  };
  const startEditingAdSpend = (monthIndex: number, currentValue: number) => {
    if (monthIndex === 0) return;
    setEditingAdSpend(monthIndex);
    setEditAdSpendValue(currentValue.toString());
  };
  const saveAdSpendEdit = () => {
    if (editingAdSpend !== null) {
      const value = parseFloat(editAdSpendValue) || 0;
      handleAdSpendChange(editingAdSpend, value);
      setEditingAdSpend(null);
      setEditAdSpendValue('');
    }
  };
  const cancelAdSpendEdit = () => {
    setEditingAdSpend(null);
    setEditAdSpendValue('');
  };
  const handleAdSpendKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      saveAdSpendEdit();
    } else if (e.key === 'Escape') {
      cancelAdSpendEdit();
    }
  };
  const adjustedRows = useMemo(() => {
    if (!halfSales && !doubleAttrition) return rows;
    return rows.map(row => {
      let newRow = {
        ...row
      };
      if (halfSales) {
        newRow.totalSales = row.totalSales * 0.5;
        newRow.grossProfit = row.grossProfit * 0.5;
        newRow.grossRevenue = row.grossRevenue * 0.5;
      }
      if (doubleAttrition) {
        newRow.launched = row.launched * 0.5;
        newRow.active30d = row.active30d * 0.5;
        newRow.active60d = row.active60d * 0.5;
        newRow.active90d = row.active90d * 0.5;
        newRow.veterans = row.veterans * 0.5;
        const retentionMultiplier = 0.5;
        const salesMultiplier = halfSales ? 0.5 : 1;
        newRow.totalSales = row.totalSales * retentionMultiplier * salesMultiplier;
        newRow.grossProfit = row.grossProfit * retentionMultiplier * salesMultiplier;
        newRow.grossRevenue = row.grossRevenue * retentionMultiplier * salesMultiplier;
      }
      newRow.netProfit = newRow.grossProfit - row.totalCost;
      return newRow;
    });
  }, [rows, halfSales, doubleAttrition]);
  const finalRows = useMemo(() => {
    let cumProfit = adjustedRows[0].cumProfit;
    return adjustedRows.map((row, idx) => {
      if (idx === 0) return row;
      cumProfit += row.netProfit;
      return {
        ...row,
        cumProfit
      };
    });
  }, [adjustedRows]);
  const displayRows = useMemo(() => {
    // Skip the Launch row (index 0) and show only the selected number of months
    return finalRows.slice(1, timeframe + 1);
  }, [finalRows, timeframe]);
  useEffect(() => {
    checkScrollButtons();
    const container = scrollContainerRef.current;
    if (container) {
      container.addEventListener('scroll', checkScrollButtons);
      window.addEventListener('resize', checkScrollButtons);
      return () => {
        container.removeEventListener('scroll', checkScrollButtons);
        window.removeEventListener('resize', checkScrollButtons);
      };
    }
  }, [timeframe]);
  const metricGroups = [{
    id: 'recruiting',
    label: 'Recruiting',
    color: 'bg-blue-50',
    collapsible: true,
    metrics: [{
      key: 'hires',
      label: 'Hired Reps',
      format: (v: number) => v.toLocaleString()
    }]
  }, {
    id: 'activeReps',
    label: 'Active Reps',
    color: 'bg-purple-50',
    collapsible: true,
    metrics: [{
      key: 'launched',
      label: 'Launched',
      format: (v: number) => v.toFixed(1)
    }, {
      key: 'active30d',
      label: 'Active 30d+',
      format: (v: number) => v.toFixed(1)
    }, {
      key: 'active60d',
      label: 'Active 60d+',
      format: (v: number) => v.toFixed(1)
    }, {
      key: 'active90d',
      label: 'Active 90d+',
      format: (v: number) => v.toFixed(1)
    }, {
      key: 'teamSize',
      label: 'Total Team Size',
      format: (v: number) => v.toFixed(1),
      bold: true,
      centered: true
    }]
  }, {
    id: 'investment',
    label: 'Investment',
    color: 'bg-red-50',
    collapsible: true,
    metrics: [{
      key: 'adSpend',
      label: 'Ad Spend',
      format: (v: number) => fmtMoney(v),
      clickToEdit: true
    }, {
      key: 'recruitingTimeCost',
      label: 'Recruiting Time Cost',
      format: (v: number) => fmtMoney(v)
    }, {
      key: 'totalCost',
      label: 'Total Investment',
      format: (v: number) => fmtMoney(v),
      bold: true
    }]
  }, {
    id: 'revenue',
    label: 'Revenue/Profit',
    color: 'bg-green-50',
    collapsible: true,
    metrics: [{
      key: 'totalSales',
      label: 'Total Sales',
      format: (v: number) => v.toFixed(1),
      bold: true
    }, {
      key: 'realizationRate',
      label: 'Install Rate',
      format: (v: number) => `${(v * 100).toFixed(0)}%`
    }, {
      key: 'grossRevenue',
      label: 'Gross Revenue',
      format: (v: number) => fmtMoney(v)
    }, {
      key: 'grossProfit',
      label: 'Gross Profit',
      format: (v: number) => fmtMoney(v)
    }, {
      key: 'netProfit',
      label: 'Net Profit',
      format: (v: number) => fmtMoney(v),
      highlight: true
    }, {
      key: 'cumProfit',
      label: 'Profit To Date',
      format: (v: number) => fmtMoney(v),
      highlight: true
    }]
  }];
  return <div className="bg-white border-2 border-gray-200 rounded-lg shadow-lg overflow-hidden" data-id="element-371">
      {/* Controls Header */}
      <div className="p-4 md:p-6 border-b-2 border-gray-100 bg-gray-50" data-id="element-372">
        {/* Single Row: All controls in one line */}
        <div className="flex flex-wrap items-center gap-2" data-id="element-373">
          {/* Timeframe Selector */}
          <div className="inline-flex bg-white rounded-lg border border-gray-200 p-1 shadow-sm" data-id="element-374">
            {[3, 6, 12].map(months => <button key={months} onClick={() => setTimeframe(months as 3 | 6 | 12)} className={`px-2 md:px-3 py-1 rounded-md text-xs font-medium transition-all ${timeframe === months ? 'bg-navy text-white shadow-sm' : 'text-gray-600 hover:bg-gray-50'}`} data-id="element-375">
                {months}M
              </button>)}
          </div>

          {/* Scenario Toggles - matching style from ProfitChart */}
          <button onClick={() => setHalfSales(!halfSales)} className={`px-2 md:px-3 py-1 rounded text-xs font-semibold transition-all border-2 ${halfSales ? 'bg-red-500 text-white border-red-600' : 'bg-white text-gray-700 border-gray-300 hover:border-red-400'}`} data-id="element-376">
            {halfSales ? '✓ ' : ''}50% Sales
          </button>
          <button onClick={() => setDoubleAttrition(!doubleAttrition)} className={`px-2 md:px-3 py-1 rounded text-xs font-semibold transition-all border-2 ${doubleAttrition ? 'bg-orange-500 text-white border-orange-600' : 'bg-white text-gray-700 border-gray-300 hover:border-orange-400'}`} data-id="element-377">
            {doubleAttrition ? '✓ ' : ''}2x Attrition
          </button>
        </div>
      </div>

      {/* Table Container with Scroll Buttons */}
      <div className="relative" data-id="element-378">
        {/* Left Scroll Button */}
        {canScrollLeft && <button onClick={() => scroll('left')} className="absolute left-32 top-1/2 -translate-y-1/2 z-30 bg-white/90 text-navy p-1.5 rounded-full shadow-md hover:bg-white transition-all border border-gray-200" aria-label="Scroll left" data-id="element-379">
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" data-id="element-380">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M15 19l-7-7 7-7" data-id="element-381" />
            </svg>
          </button>}

        {/* Right Scroll Button */}
        {canScrollRight && <button onClick={() => scroll('right')} className="absolute right-4 top-1/2 -translate-y-1/2 z-30 bg-white/90 text-navy p-1.5 rounded-full shadow-md hover:bg-white transition-all border border-gray-200" aria-label="Scroll right" data-id="element-382">
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" data-id="element-383">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M9 5l7 7-7 7" data-id="element-384" />
            </svg>
          </button>}

        <div ref={scrollContainerRef} className="overflow-x-auto" data-id="element-385">
          <table className="w-full border-collapse text-sm min-w-[600px]" data-id="element-386">
            <thead data-id="element-387">
              <tr className="bg-navy text-white" data-id="element-388">
                <th className="sticky left-0 z-20 bg-navy text-xs font-bold uppercase tracking-wide p-2 text-left w-48" data-id="element-389">
                  Metric
                </th>
                {displayRows.map(row => <th key={row.period} className="bg-navy text-xs font-bold uppercase tracking-wide p-2 text-center whitespace-nowrap min-w-[90px]" data-id="element-390">
                    {row.period}
                  </th>)}
              </tr>
            </thead>
            <tbody className="bg-white" data-id="element-391">
              {metricGroups.map(group => {
              const isExpanded = expandedSections[group.id as keyof typeof expandedSections];
              const showDetails = !group.collapsible || isExpanded;
              return <Fragment key={group.id} data-id="element-392">
                    {/* Section Header */}
                    <tr className={`${group.color} border-b border-gray-200`} data-id="element-393">
                      <td className={`sticky left-0 z-10 ${group.color} p-2 font-bold text-gray-900 w-48 text-xs cursor-pointer hover:brightness-95 transition-all whitespace-nowrap`} onClick={() => toggleSection(group.id as keyof typeof expandedSections)} data-id="element-394">
                        <div className="flex items-center gap-1" data-id="element-395">
                          <span className="text-navy text-xs" data-id="element-396">
                            {isExpanded ? '▼' : '▶'}
                          </span>
                          <span className="uppercase tracking-wide text-xs" data-id="element-397">
                            {group.label}
                          </span>
                        </div>
                      </td>
                      {displayRows.map((row, rowIdx) => {
                    const isEvenColumn = rowIdx % 2 === 0;
                    const zebraClass = isEvenColumn ? '' : 'bg-black/5';
                    return <td key={row.period} className={`${group.color} ${zebraClass} p-2 border-b border-gray-200`} data-id="element-398" />;
                  })}
                    </tr>

                    {/* Metrics Rows */}
                    {showDetails && group.metrics.map((metric, metricIdx) => {
                  const isLastInGroup = metricIdx === group.metrics.length - 1;
                  const borderClass = isLastInGroup ? 'border-b border-gray-300' : 'border-b border-gray-100';
                  const isEvenRow = metricIdx % 2 === 0;
                  const rowBgClass = isEvenRow ? group.color : group.color;
                  return <tr key={metric.key} className={`hover:brightness-95 transition-all h-8 ${rowBgClass}`} data-id="element-399">
                            <td className={`sticky left-0 z-10 ${group.color} p-2 pl-6 text-gray-700 ${metric.bold ? 'font-bold text-xs' : 'font-medium text-xs'} ${borderClass} w-48`} data-id="element-400">
                              {metric.label}
                            </td>
                            {displayRows.map((row, rowIdx) => {
                      let value: number;
                      if (metric.key === 'teamSize') {
                        const launched0to30 = row.launched * 0.5;
                        const active30to60 = row.active30d;
                        const active60to90 = row.active60d;
                        const active90plus = row.active90d + row.veterans;
                        value = launched0to30 + active30to60 + active60to90 + active90plus;
                      } else if (metric.key === 'recruitingTimeCost') {
                        value = row.hireCost;
                      } else {
                        value = row[metric.key as keyof RowData] as number;
                      }
                      const formatted = metric.format(value);
                      const isEvenColumn = rowIdx % 2 === 0;
                      const columnShade = isEvenColumn ? '' : 'bg-black/5';
                      let cellClass = `p-2 text-center whitespace-nowrap ${borderClass} relative min-w-[90px] text-xs ${group.color} ${columnShade}`;
                      if (metric.highlight) {
                        cellClass += value >= 0 ? ' text-green-600 font-bold' : ' text-red-600 font-bold';
                      } else if (metric.bold) {
                        cellClass += ' font-bold text-gray-900';
                      } else {
                        cellClass += ' text-gray-600';
                      }
                      let displayValue: React.ReactNode = formatted;
                      // Handle editable ad spend
                      if (metric.key === 'adSpend' && metric.clickToEdit) {
                        const actualMonthIndex = rowIdx + 1;
                        if (editingAdSpend === actualMonthIndex) {
                          displayValue = <div className="flex justify-center items-center gap-1" data-id="element-401">
                                      <input type="number" min="0" step="100" value={editAdSpendValue} onChange={e => setEditAdSpendValue(e.target.value)} onKeyDown={handleAdSpendKeyDown} onBlur={saveAdSpendEdit} autoFocus className="w-16 text-center border-2 border-amber rounded px-1 py-0.5 text-xs focus:border-amber outline-none bg-white shadow-sm" data-id="element-402" />
                                      {rowIdx < displayRows.length - 1 && <button onClick={() => {
                              saveAdSpendEdit();
                              handlePushAdSpendRight(actualMonthIndex);
                            }} className="w-4 h-4 flex items-center justify-center transition-colors hover:bg-amber/10 rounded text-amber text-xs" title="Apply to all future months" data-id="element-403">
                                          →
                                        </button>}
                                    </div>;
                        } else {
                          displayValue = <div className="flex justify-center items-center gap-1 group/edit" data-id="element-404">
                                      <span onClick={() => startEditingAdSpend(actualMonthIndex, value)} className="cursor-pointer hover:text-amber hover:underline decoration-dotted underline-offset-2 transition-colors" data-id="element-405">
                                        {formatted}
                                      </span>
                                      {rowIdx < displayRows.length - 1 ? <button onClick={() => handlePushAdSpendRight(actualMonthIndex)} className="w-3 h-3 flex items-center justify-center text-amber opacity-0 group-hover/edit:opacity-100 transition-opacity text-xs" title="Apply to all future months" data-id="element-406">
                                          →
                                        </button> : <div className="w-3 h-3" data-id="element-407"></div>}
                                    </div>;
                        }
                      }
                      return <td key={row.period} className={cellClass} data-id="element-408">
                                  {displayValue}
                                </td>;
                    })}
                          </tr>;
                })}
                  </Fragment>;
            })}
            </tbody>
          </table>
        </div>
      </div>
    </div>;
}